/**
 * useJsonRenderStream Hook
 *
 * Consumes SSE stream from the json-render endpoint and provides
 * the UI tree for rendering with json-render.
 */

import { useState, useCallback, useRef } from 'react';

/**
 * Parse SSE data from stream
 */
function parseSSEMessage(line) {
  if (!line.startsWith('data: ')) return null;
  try {
    return JSON.parse(line.slice(6));
  } catch {
    return null;
  }
}

/**
 * Hook for consuming json-render streaming responses
 */
export function useJsonRenderStream({
  sessionId,
  baseUrl = '',
  authToken,
  usePublicEndpoint = false,
  onError,
  onComplete,
}) {
  const [tree, setTree] = useState(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(null);
  const abortControllerRef = useRef(null);

  /**
   * Send a message and receive streaming UI response
   */
  const send = useCallback(async (message, context = {}) => {
    // Abort any existing stream
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Reset state
    setTree(null);
    setError(null);
    setProgress(null);
    setIsStreaming(true);

    // Create new abort controller
    abortControllerRef.current = new AbortController();

    try {
      // Use public endpoint for testing or secure endpoint for production
      const endpoint = usePublicEndpoint
        ? `${baseUrl}/api/test/stream`
        : `${baseUrl}/api/secure/sessions/${sessionId}/stream`;

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
        },
        body: JSON.stringify({
          message,
          context: {
            cartItemCount: context.cartItemCount || 0,
            preferences: context.preferences || {},
          },
          ...(context.tenantId ? { tenantId: context.tenantId } : {}),
        }),
        signal: abortControllerRef.current.signal,
      });

      if (!response.ok) {
        throw new Error(`Stream failed: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      let reading = true;
      while (reading) {
        const { done, value } = await reader.read();

        if (done) {
          reading = false;
          continue;
        }

        // Decode chunk and add to buffer
        buffer += decoder.decode(value, { stream: true });

        // Process complete lines
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // Keep incomplete line in buffer

        for (const line of lines) {
          if (!line.trim()) continue;

          const data = parseSSEMessage(line);
          if (!data) continue;

          switch (data.type) {
            case 'progress':
              console.log('[Stream] Progress:', data.message);
              setProgress(data.message);
              break;

            case 'tree':
              console.log('[Stream] Tree received:', Object.keys(data.data?.elements || {}).length, 'elements');
              setTree(data.data);
              break;

            case 'element':
              // Incremental element update (full element replacement)
              setTree((prevTree) => {
                if (!prevTree) {
                  return {
                    root: data.id,
                    elements: { [data.id]: data },
                  };
                }
                return {
                  ...prevTree,
                  elements: {
                    ...prevTree.elements,
                    [data.id]: data,
                  },
                };
              });
              break;

            case 'tree_update':
              // Merge props into existing element (for progressive streaming)
              console.log('[Stream] Tree update:', data.data?.elementKey, Object.keys(data.data?.props || {}));
              setTree((prevTree) => {
                if (!prevTree || !data.data?.elementKey) return prevTree;
                const { elementKey, props: newProps } = data.data;
                const existingElement = prevTree.elements[elementKey];
                if (!existingElement) {
                  console.log('[Stream] Element not found:', elementKey);
                  return prevTree;
                }
                console.log('[Stream] Updating element:', elementKey, 'with props:', Object.keys(newProps));
                return {
                  ...prevTree,
                  elements: {
                    ...prevTree.elements,
                    [elementKey]: {
                      ...existingElement,
                      props: {
                        ...existingElement.props,
                        ...newProps,
                      },
                    },
                  },
                };
              });
              break;

            case 'clarification':
              // CLM clarifying question - add to tree as a component
              console.log('[Stream] Clarification:', data.question, data.options);
              setTree((prevTree) => {
                if (!prevTree) return prevTree;
                // Add clarification element to the tree
                return {
                  ...prevTree,
                  elements: {
                    ...prevTree.elements,
                    clarification: {
                      key: 'clarification',
                      type: 'ClarificationPrompt',
                      props: {
                        question: data.question,
                        options: data.options || [],
                      },
                      parentKey: 'msg1',
                    },
                  },
                };
              });
              break;

            case 'conversation':
              // Conversational response (not a product search)
              // Build a UI tree with ConversationalMessage component
              console.log('[Stream] Conversation:', data.message?.slice(0, 50));
              setTree({
                root: 'msg1',
                elements: {
                  msg1: {
                    key: 'msg1',
                    type: 'ConversationalMessage',
                    props: {
                      content: data.message || '',
                    },
                    children: ['suggestions'],
                  },
                  suggestions: {
                    key: 'suggestions',
                    type: 'SuggestionChips',
                    props: {
                      suggestions: (data.suggestions || []).map(s => ({
                        label: s.label || s.category || 'Explore',
                        // Preserve action if set (for connect_wallet, etc.), otherwise use query
                        ...(s.action ? { action: s.action } : { query: s.query || s.query_hint || s.label }),
                      })),
                    },
                    parentKey: 'msg1',
                  },
                },
              });
              break;

            case 'done':
              console.log('[Stream] Done:', data);
              setProgress(null);
              if (onComplete) {
                onComplete(data);
              }
              break;

            case 'error': {
              const err = new Error(data.message);
              setError(err);
              if (onError) {
                onError(err);
              }
              break;
            }

            default:
              console.log('[Stream] Unknown event type:', data.type);
          }
        }
      }
    } catch (err) {
      if (err.name === 'AbortError') {
        console.log('[Stream] Aborted');
      } else {
        console.error('[Stream] Error:', err);
        setError(err);
        if (onError) {
          onError(err);
        }
      }
    } finally {
      setIsStreaming(false);
      abortControllerRef.current = null;
    }
  }, [sessionId, baseUrl, authToken, usePublicEndpoint, onError, onComplete]);

  /**
   * Clear the current tree
   */
  const clear = useCallback(() => {
    setTree(null);
    setError(null);
    setProgress(null);
  }, []);

  /**
   * Abort the current stream
   */
  const abort = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setIsStreaming(false);
  }, []);

  return {
    tree,
    isStreaming,
    error,
    progress,
    send,
    clear,
    abort,
  };
}

export default useJsonRenderStream;
